import { useFormik } from "formik";
import CachedIcon from "mdi-react/CachedIcon";
import React, { useState } from "react";
import {
  Modal,
  Form,
  Button,
  Table,
  Row,
  Col,
  Dropdown,
} from "react-bootstrap";
import { useMutation, useQuery } from "react-query";
import { toast } from "react-toastify";
import * as yup from "yup";
import { useBackendUrl } from "../../utils/hooks";
import ConfirmDialog from "../ConfirmDialogue";
import { DeleteIcon, EditIcon } from "../Icons";
import DotsVerticalIcon from "mdi-react/DotsVerticalIcon";
import { format } from "date-fns";
import { Plus } from "lucide-react";
import Datetime from "react-datetime";
import { FilesAttachments } from "../../utils/Attachments";

// ── Inner modal: the Add / Edit Contract form ────────────────────────
function DistributionFormModal({
  show,
  setShow,
  vendor,
  editingRecord,
  setEditingRecord,
  refetch,
}) {
  const backendUrl = useBackendUrl();

  const addDistribution = async (payload) => {
    let response = await fetch(`${backendUrl}/api/vendors/distribution`, {
      method: "POST",
      credentials: "include",
      body: payload,
    });
    if (!response.ok) {
      response = await response.json();
      throw new Error(response.message);
    }
    return response.json();
  };

  const addDistributionMutation = useMutation(
    (payload) => addDistribution(payload),
    {
      onSuccess: ({ message }) => {
        toast.success(message);
        formik.resetForm();
        refetch();
        setShow(false);
      },
      onError: ({ message = "" }) =>
        toast.error(`Unable to perform action: ${message}`),
    },
  );

  const editDistribution = async ({ id, payload }) => {
    let response = await fetch(`${backendUrl}/api/vendors/distribution/${id}`, {
      method: "PATCH",
      credentials: "include",
      body: payload,
    });
    if (!response.ok) {
      response = await response.json();
      throw new Error(response.message);
    }
    return response.json();
  };

  const editDistributionMutation = useMutation(
    (args) => editDistribution(args),
    {
      onSuccess: ({ message }) => {
        toast.success(message);
        formik.resetForm();
        setEditingRecord(null);
        refetch();
        setShow(false);
      },
      onError: ({ message = "" }) =>
        toast.error(`Unable to perform action: ${message}`),
    },
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      Distributor_Contract: editingRecord?.Distributor_Contract || "",
      Distributor_Contract_Expiry_Date: editingRecord?.Distributor_Contract_Expiry_Date
        ? format(
            new Date(editingRecord.Distributor_Contract_Expiry_Date),
            "yyyy-MM-dd",
          )
        : "",
      Distributor_Code: editingRecord?.Distributor_Code || "",
      // existing files (with fileId/systemFileName) pre-populate here on edit
      Attachments: editingRecord?.Attachments || [],
    },
    validationSchema: yup.object().shape({
      Distributor_Code: yup.string().required("Distributor Code is required"),
    }),
    onSubmit: async (values) => {
      const isEditing = !!editingRecord;

      if (
        await ConfirmDialog({
          title: isEditing
            ? "Update Distribution Contract"
            : "Add Distribution Contract",
          description: isEditing
            ? `Update contract ${editingRecord.Distributor_Code}?`
            : `Link contract to ${vendor.CompanyName}?`,
        })
      ) {
        const formData = new FormData();
        formData.append("Vendor", vendor.Vendor_ID);
        formData.append("Distributor_Contract", values.Distributor_Contract);
        formData.append(
          "Distributor_Contract_Expiry_Date",
          values.Distributor_Contract_Expiry_Date,
        );
        formData.append("Distributor_Code", values.Distributor_Code);

        // split: already-uploaded files (kept) vs newly added local File objects
        const existingKept = (values.Attachments || []).filter(
          (f) => f?.fileId,
        );
        const newFiles = (values.Attachments || []).filter((f) => !f?.fileId);

        if (isEditing) {
          // tells backend exactly which previously-saved files to keep;
          // anything removed from this list gets deleted server-side
          formData.append("ExistingAttachments", JSON.stringify(existingKept));
        }

        newFiles.forEach((file) => {
          formData.append("Attachments", file);
        });

        if (isEditing) {
          editDistributionMutation.mutate({
            id: editingRecord.id,
            payload: formData,
          });
        } else {
          addDistributionMutation.mutate(formData);
        }
      }
    },
  });

  const isLoading =
    addDistributionMutation.isLoading || editDistributionMutation.isLoading;

  const handleClose = () => {
    setEditingRecord(null);
    formik.resetForm();
    setShow(false);
  };

  return (
    <Modal show={show} onHide={handleClose} centered animation={false}>
      <Modal.Header closeButton>
        <Modal.Title className="h5">
          {editingRecord ? "Edit Contract" : "Add Contract"}
        </Modal.Title>
      </Modal.Header>

      <Form noValidate onSubmit={formik.handleSubmit} autoComplete="off">
        <Modal.Body>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Distributor Code</Form.Label>
                <Form.Control
                  type="text"
                  name="Distributor_Code"
                  placeholder="Enter Distributor Code"
                  value={formik.values.Distributor_Code}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={
                    formik.touched.Distributor_Code &&
                    !!formik.errors.Distributor_Code
                  }
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.Distributor_Code}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Contract Expiry Date</Form.Label>
                <Datetime
                  dateFormat="MMM DD, YYYY"
                  timeFormat={false}
                  closeOnSelect={true}
                  closeOnClickOutside={true}
                  name="Distributor_Contract_Expiry_Date"
                  inputProps={{
                    className: `date-input form-control ${
                      formik.touched.Distributor_Contract_Expiry_Date &&
                      !!formik.errors.Distributor_Contract_Expiry_Date
                        ? "is-invalid"
                        : ""
                    }`,
                    placeholder: "Select date",
                    readOnly: true,
                  }}
                  value={formik.values.Distributor_Contract_Expiry_Date}
                  onChange={(date) => {
                    formik.setFieldValue(
                      "Distributor_Contract_Expiry_Date",
                      date,
                      true,
                    );
                  }}
                  onBlur={() =>
                    formik.setFieldTouched(
                      "Distributor_Contract_Expiry_Date",
                      true,
                    )
                  }
                />
                {formik.touched.Distributor_Contract_Expiry_Date &&
                !!formik.errors.Distributor_Contract_Expiry_Date ? (
                  <span className="custom-invalid-feedback">
                    {formik.errors.Distributor_Contract_Expiry_Date}
                  </span>
                ) : null}
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Contract Name / Reference</Form.Label>
            <Form.Control
              type="text"
              name="Distributor_Contract"
              placeholder="Enter Contract Name or Reference"
              value={formik.values.Distributor_Contract}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Attachments</Form.Label>

            <FilesAttachments
              files={formik.values.Attachments || []}
              setFiles={(newFiles) =>
                formik.setFieldValue("Attachments", newFiles)
              }
              url={`${backendUrl}/static/vendor-distribution-management`}
              accept="image/*"
              colWidth="4"
            />

            <Form.Text className="text-muted">
              You can select multiple files.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="white"
            className="border bg-white px-4"
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            className="d-flex align-items-center gap-2"
            type="submit"
            disabled={isLoading}
          >
            {isLoading
              ? "Please wait…"
              : editingRecord
              ? "Save Changes"
              : "Add Contract"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

// ── Outer modal: vendor distribution list ────────────────────────────
export default function VendorDistributionModal({ show, setShow, vendor }) {
  const backendUrl = useBackendUrl();
  const [editingRecord, setEditingRecord] = useState(null);
  const [showFormModal, setShowFormModal] = useState(false);

  // ── GET ───────────────────────────────────────────────────────
  const fetchDistributions = async () => {
    let response = await fetch(
      `${backendUrl}/api/vendors/distribution?Vendor=${encodeURIComponent(
        vendor.Vendor_ID,
      )}`,
      {
        method: "GET",
        headers: {
          Accept: "Application/json",
          "Content-Type": "Application/json",
        },
        credentials: "include",
      },
    );
    if (!response.ok) {
      response = await response.json();
      throw new Error(response.message);
    }
    const { data } = await response.json();
    return data;
  };

  const { data = { records: [], count: 0 }, refetch } = useQuery(
    ["VENDOR_DISTRIBUTION", vendor?.Vendor_ID],
    () => fetchDistributions(),
    { keepPreviousData: true, enabled: !!vendor?.Vendor_ID },
  );

  // ── DELETE mutation (delete whole record) ─────────────────────
  const deleteDistribution = async (id) => {
    let response = await fetch(`${backendUrl}/api/vendors/distribution/${id}`, {
      method: "DELETE",
      credentials: "include",
    });
    if (!response.ok) {
      response = await response.json();
      throw new Error(response.message);
    }
    return response.json();
  };

  const deleteDistributionMutation = useMutation(
    (id) => deleteDistribution(id),
    {
      onSuccess: ({ message }) => {
        toast.success(message);
        refetch();
      },
      onError: ({ message = "" }) =>
        toast.error(`Unable to perform action: ${message}`),
    },
  );

  const handleEdit = (record) => {
    setEditingRecord(record);
    setShowFormModal(true);
  };

  const handleAddNew = () => {
    setEditingRecord(null);
    setShowFormModal(true);
  };

  const handleDelete = async (record) => {
    if (
      await ConfirmDialog({
        title: "Remove Distribution Contract",
        description: `Remove contract ${record.Distributor_Code}?`,
        type: "danger",
      })
    ) {
      deleteDistributionMutation.mutate(record.id);
    }
  };

  return (
    <>
      <Modal
        show={show}
        onHide={() => setShow(false)}
        size="lg"
        centered
        animation={false}
        autoFocus={false}
      >
        <Modal.Header
          closeButton
          className="d-flex align-items-center justify-content-between"
        >
          <Modal.Title className="h5">{vendor?.CompanyName}</Modal.Title>
          <div className="d-flex gap-3 justify-content-end">
            <Button variant="" className="text-primary ms-2" onClick={refetch}>
              <CachedIcon />
            </Button>
            <Button
              variant="primary"
              className="d-flex align-items-center gap-2"
              onClick={handleAddNew}
            >
              <Plus style={{ width: 16 }} /> Add Contract
            </Button>
          </div>
        </Modal.Header>

        <Modal.Body>
          {/* ── Table ── */}
          <Table borderless striped responsive className="product-table border">
            <thead>
              <tr>
                <th />
                <th>Distributor Code</th>
                <th>Contract Ref</th>
                <th>Expiry Date</th>
                <th>Attachments</th>
              </tr>
            </thead>
            <tbody>
              {data.records.map((el, index) => (
                <tr key={index}>
                  <td style={{ whiteSpace: "nowrap", width: "1%" }}>
                    <Dropdown alignRight drop="end">
                      <Dropdown.Toggle
                        variant=""
                        className="bg-white border-0"
                        bsPrefix="print more"
                      >
                        <DotsVerticalIcon />
                      </Dropdown.Toggle>
                      <Dropdown.Menu
                        popperConfig={{ strategy: "fixed" }}
                        renderOnMount
                        className="dropdown-with-icons"
                      >
                        <Dropdown.Item
                          as="button"
                          onClick={() => handleEdit(el)}
                        >
                          <EditIcon /> Edit
                        </Dropdown.Item>
                        <Dropdown.Item
                          as="button"
                          className="text-danger"
                          onClick={() => handleDelete(el)}
                          disabled={deleteDistributionMutation.isLoading}
                        >
                          <DeleteIcon /> Delete
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </td>
                  <td>{el.Distributor_Code}</td>
                  <td>{el.Distributor_Contract || "—"}</td>
                  <td>
                    {el.Distributor_Contract_Expiry_Date
                      ? new Date(
                          el.Distributor_Contract_Expiry_Date,
                        ).toLocaleDateString()
                      : "—"}
                  </td>
                  <td>
                    {el.Attachments && el.Attachments.length > 0 ? (
                      <Dropdown>
                        <Dropdown.Toggle variant="outline-secondary" size="sm">
                          {el.Attachments.length} file
                          {el.Attachments.length > 1 ? "s" : ""}
                        </Dropdown.Toggle>
                        <Dropdown.Menu
                          popperConfig={{ strategy: "fixed" }}
                          renderOnMount
                        >
                          {el.Attachments.map((file, i) => (
                            <Dropdown.Item
                              key={i}
                              href={`${backendUrl}/static/vendor-distribution-management/${
                                file.systemFileName
                              }`}
                              target="_blank"
                              rel="noreferrer"
                            >
                              {file.name}
                            </Dropdown.Item>
                          ))}
                        </Dropdown.Menu>
                      </Dropdown>
                    ) : (
                      "No Attachments"
                    )}
                  </td>
                </tr>
              ))}
              {data.records.length === 0 && (
                <tr>
                  <td colSpan="5" className="text-center text-muted py-3">
                    No distribution contracts linked yet
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Modal.Body>
      </Modal>

      {showFormModal && (
        <DistributionFormModal
          show={showFormModal}
          setShow={setShowFormModal}
          vendor={vendor}
          editingRecord={editingRecord}
          setEditingRecord={setEditingRecord}
          refetch={refetch}
        />
      )}
    </>
  );
}
